import React, { useEffect, useState } from 'react';
import InputGroup from 'react-bootstrap/InputGroup';
import Switcher from 'components/ui/switcher/Switcher';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import { MockService } from 'types/rest/mockServiceTypes';
import { MockParameterState } from '../MockParameters';
import acApi from 'axios/ac/acApi';
import { AccountSettingsDto } from 'types/rest/accountSettingsTypes';
import { convertToNumber } from 'utils/commonUtil';
import { getHarborDefaultHeapMb } from 'utils/mockServiceUtils';

interface Props {
  mockService: MockService;
  onUpdateState: (state: MockParameterState, debounce?: boolean) => void;
  disabled?: boolean;
}

const MemorySettings = ({ mockService, onUpdateState, disabled }: Props) => {
  const [enabled, setEnabled] = useState(mockService.jvmMemoryOverrideEnabled ?? false);
  const [minJvmHeapMb, setMinJvmHeapMb] = useState<number | null>(null);
  const [maxJvmHeapMb, setMaxJvmHeapMb] = useState<number | null>(null);
  const [xmsAboveXmx, setXmsAboveXmx] = useState(false);

  useEffect(() => {
    let cancelled = false;
    acApi.accountSettings
      .getSettings()
      .then(response => {
        if (cancelled) return;
        const settings: AccountSettingsDto = response.data.result;
        setMinJvmHeapMb(settings.minJvmHeapMb ?? null);
        setMaxJvmHeapMb(settings.maxJvmHeapMb ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setMinJvmHeapMb(null);
        setMaxJvmHeapMb(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const belowAccountMin = (value: number | string) => {
    return value !== '' && minJvmHeapMb != null && minJvmHeapMb > 0 && Number(value) < minJvmHeapMb;
  };

  const exceedsAccountMax = (value: number | string) => {
    return value !== '' && maxJvmHeapMb != null && maxJvmHeapMb > 0 && Number(value) > maxJvmHeapMb;
  };

  const propagateUpdate = (fields: Partial<MockService>) => {
    const update: MockService = { ...mockService, ...fields };
    onUpdateState(update, true);
  };

  const validateXmsNotAboveXmx = (xms: number | string, xmx: number | string) => {
    // Non-blocking: the backend still rejects Xms > Xmx at save/deploy time (merge), so this is a
    // heads-up rather than a hard stop — it must not disable Save (see draft-PATCH validation removal).
    setXmsAboveXmx(xms !== '' && xmx !== '' && Number(xms) > Number(xmx));
  };

  const xmxInput = useFormInput(
    mockService.jvmOverrideXmxMb ?? '',
    (fn: CallbackFn) => {
      if (fn.isValid) {
        const value = convertToNumber(fn.value);
        propagateUpdate({ jvmOverrideXmxMb: value });
        validateXmsNotAboveXmx(xmsInput.value, fn.value);
      }
    },
    { type: ValidatorType.REQUIRED, invalidMessage: 'Xmx is required.' },
    { type: ValidatorType.INTEGER, invalidMessage: 'Xmx must be a whole number.' },
    { type: ValidatorType.MIN, value: 1, invalidMessage: 'Xmx must be a positive number.' },
  );

  const xmsInput = useFormInput(
    mockService.jvmOverrideXmsMb ?? '',
    (fn: CallbackFn) => {
      if (fn.isValid) {
        const value = convertToNumber(fn.value);
        propagateUpdate({ jvmOverrideXmsMb: value });
        validateXmsNotAboveXmx(fn.value, xmxInput.value);
      }
    },
    { type: ValidatorType.REQUIRED, invalidMessage: 'Xms is required.' },
    { type: ValidatorType.INTEGER, invalidMessage: 'Xms must be a whole number.' },
    { type: ValidatorType.MIN, value: 1, invalidMessage: 'Xms must be a positive number.' },
  );

  const handleToggle = () => {
    const nextEnabled = !enabled;
    setEnabled(nextEnabled);

    let xmx = mockService.jvmOverrideXmxMb;
    let xms = mockService.jvmOverrideXmsMb;
    if (nextEnabled && xmx == null && xms == null) {
      const harborDefaults = getHarborDefaultHeapMb(mockService.harborId, mockService.shipId);
      if (harborDefaults.xmx != null && harborDefaults.xms != null) {
        xmx = harborDefaults.xmx;
        xms = harborDefaults.xms;
        xmxInput.overrideValue(xmx);
        xmxInput.overrideValidation({ isValid: true, invalidMessage: '' });
        xmsInput.overrideValue(xms);
        xmsInput.overrideValidation({ isValid: true, invalidMessage: '' });
        validateXmsNotAboveXmx(xms, xmx);
      }
    }

    const update: MockService = {
      ...mockService,
      jvmMemoryOverrideEnabled: nextEnabled,
      jvmOverrideXmxMb: xmx,
      jvmOverrideXmsMb: xms,
    };
    onUpdateState(update);
  };

  const renderRangeWarning = (value: number | string) => {
    if (belowAccountMin(value)) {
      return (
        <p className="text-warning small">
          This value is below the account&apos;s current minimum ({minJvmHeapMb} MB) — {minJvmHeapMb} MB will be used
          instead at deploy.
        </p>
      );
    }
    if (exceedsAccountMax(value)) {
      return (
        <p className="text-warning small">
          This value exceeds the account&apos;s current maximum ({maxJvmHeapMb} MB) — {maxJvmHeapMb} MB will be used
          instead at deploy.
        </p>
      );
    }
    return null;
  };

  return (
    <div>
      <Switcher
        checked={enabled}
        onChange={handleToggle}
        label={'Override JVM Heap Settings'}
        size={'md'}
        disabled={disabled}
      />
      {enabled ? (
        <div className={'mt-3'}>
          <div style={{ maxWidth: '360px' }}>
            <label htmlFor={'ms-jvm-xmx'}>Xmx (MB)</label>
            <InputGroup className="mb-1">
              <BzFormControlWithHook
                id={'ms-jvm-xmx'}
                useFormInputHook={xmxInput}
                disabled={disabled}
                required={true}
              />
            </InputGroup>
            {renderRangeWarning(xmxInput.value)}
            {xmsAboveXmx && <p className="text-warning small">Xms cannot be greater than Xmx.</p>}
          </div>
          <div style={{ maxWidth: '360px' }} className={'mt-3'}>
            <label htmlFor={'ms-jvm-xms'}>Xms (MB)</label>
            <InputGroup className="mb-1">
              <BzFormControlWithHook
                id={'ms-jvm-xms'}
                useFormInputHook={xmsInput}
                disabled={disabled}
                required={true}
              />
            </InputGroup>
            {renderRangeWarning(xmsInput.value)}
            {xmsAboveXmx && <p className="text-warning small">Xms cannot be greater than Xmx.</p>}
          </div>
        </div>
      ) : (
        <div className="text-muted small mt-2">
          Inherits this location&apos;s current default Xmx/Xms.
          {maxJvmHeapMb != null &&
            maxJvmHeapMb > 0 &&
            ` If overridden, values must fit within ${
              minJvmHeapMb != null && minJvmHeapMb > 0 ? `${minJvmHeapMb}–` : ''
            }${maxJvmHeapMb} MB.`}
        </div>
      )}
    </div>
  );
};

export default MemorySettings;
